import { Link } from "react-router-dom";
import {
  Package,
  Globe2,
  Clock,
  IndianRupee,
  CloudRain,
  ShieldCheck,
  Info,
  Zap,
  Truck,
  Plane,
  Mail,
  MessageCircle,
} from "lucide-react";

const HIGHLIGHTS = [
  { icon: Package, title: "Books Delivered Worldwide", body: "Enjoy your favourite books, wherever you are. From metros to small towns, and across international borders.", accent: "bg-[#e0e7ff] text-[#3730a3] border-[#c7d2fe]" },
  { icon: Globe2, title: "Global Shipping", body: "We ship to customers across the world with reliable delivery options and trusted courier partners.", accent: "bg-[#dbeafe] text-[#1e40af] border-[#bfdbfe]" },
  { icon: Clock, title: "Delivery Time Varies", body: "Shipping times depend on your location and the delivery method you choose at checkout.", accent: "bg-[#fef3c7] text-[#92400e] border-[#fde68a]" },
  { icon: IndianRupee, title: "Fair Shipping Rates", body: "Shipping costs are calculated based on your destination and the selected shipping service.", accent: "bg-[#dcfce7] text-[#166534] border-[#bbf7d0]" },
  { icon: CloudRain, title: "Possible Delivery Delays", body: "Customs clearance, weather conditions, or carrier issues may occasionally impact delivery schedules.", accent: "bg-[#ffedd5] text-[#9a3412] border-[#fed7aa]" },
  { icon: ShieldCheck, title: "Safe & Secure Packing", body: "We carefully pack every order to help ensure your books arrive in excellent condition.", accent: "bg-[#fae8ff] text-[#86198f] border-[#f5d0fe]" },
];

const DOMESTIC_TIERS = [
  { icon: Zap, title: "Processing Time", time: "1–2 Business Days", note: "After payment confirmation. Extends to 3 days during holidays, festivals, or high-demand periods." },
  { icon: Truck, title: "Standard Shipping", time: "7–10 Business Days", note: "Reliable and affordable. Ideal for most orders across India." },
  { icon: Plane, title: "Express Shipping", time: "4–7 Business Days", note: "Faster delivery for time-sensitive orders. Additional charges apply." },
];

const INTL_TIERS = [
  { icon: Mail, title: "Registered Airmail", time: "~21 Days", price: "", note: "Available for all international orders." },
  { icon: Plane, title: "International Courier", time: "5–7 Business Days", price: "Extra Charges", note: "Faster door-to-door delivery. Time depends on destination." },
];

export default function ShippingPolicy() {
  return (
    <div className="bg-[#fafaf9] min-h-screen" data-testid="shipping-policy-page">
      {/* HERO */}
      <section className="bg-[#09090b] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 relative">
          <div className="inline-flex items-center gap-2 border border-[#ea580c]/40 bg-[#ea580c]/10 text-[#fb923c] px-3 py-1.5 text-xs font-bold tracking-[0.2em] uppercase mb-6">
            <Truck className="w-3.5 h-3.5" /> Shipping &amp; Delivery
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] max-w-4xl" data-testid="shipping-policy-title">
            Books to your <span className="text-[#fb923c]">doorstep</span>, wherever you are.
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#a1a1aa] max-w-2xl leading-relaxed">
            Reliable domestic and international shipping. Transparent timelines, safe packing, and tracking on Email + WhatsApp — the moment your order is dispatched.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="https://wa.me/919953272630?text=Hi%20GAINA%20THE%20PRELUDE%2C%20I%20want%20to%20know%20my%20order%20shipping%20status."
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] text-white px-5 py-3 text-sm font-bold tracking-wide uppercase transition-colors"
              data-testid="shipping-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" /> Track via WhatsApp
            </a>
            <a
              href="tel:9953272630"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white/50 text-white px-5 py-3 text-sm font-bold tracking-wide uppercase transition-colors"
              data-testid="shipping-call-btn"
            >
              Call +91 99532 72630
            </a>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="mb-10">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3">What to Expect</div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#09090b] max-w-3xl">
            Shipping done right — reliable, safe, and transparent.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shipping-highlights-grid">
          {HIGHLIGHTS.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={h.title} className="bg-white border border-[#e7e5e4] p-6 hover:border-[#09090b] transition-colors" data-testid={`shipping-card-${i}`}>
                <div className={`inline-flex w-11 h-11 items-center justify-center border ${h.accent}`}>
                  <Icon className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <div className="mt-5 font-display text-lg font-black text-[#09090b] leading-tight">{h.title}</div>
                <p className="mt-2 text-sm text-[#57534e] leading-relaxed">{h.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* DOMESTIC */}
      <section className="bg-white border-y border-[#e7e5e4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3">
                <Truck className="w-3.5 h-3.5" /> Domestic (India)
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-black text-[#09090b]">
                Quick processing. Trusted delivery.
              </h2>
            </div>
            <p className="text-sm text-[#57534e] max-w-sm">
              During holidays, festivals or high-demand periods, processing may extend to 3 business days.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#e7e5e4]" data-testid="domestic-tiers">
            {DOMESTIC_TIERS.map((t, idx) => {
              const Icon = t.icon;
              return (
                <div key={t.title} className={`p-6 md:p-8 bg-[#fafaf9] ${idx !== DOMESTIC_TIERS.length - 1 ? "md:border-r border-[#e7e5e4]" : ""}`} data-testid={`domestic-tier-${idx}`}>
                  <Icon className="w-8 h-8 text-[#ea580c]" strokeWidth={2.2} />
                  <div className="mt-4 text-xs font-bold tracking-[0.2em] uppercase text-[#71717a]">{t.title}</div>
                  <div className="mt-1 font-display text-2xl font-black text-[#09090b]">{t.time}</div>
                  <p className="mt-3 text-sm text-[#57534e] leading-relaxed">{t.note}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 bg-[#fff7ed] border-l-4 border-[#ea580c] p-5 flex gap-4">
            <Info className="w-5 h-5 text-[#ea580c] shrink-0 mt-0.5" />
            <div className="text-sm text-[#7c2d12] leading-relaxed">
              Your order tracking details will be shared with you via <strong>Email</strong> and <strong>WhatsApp</strong> as soon as your order is dispatched. Expect possible delays around official holidays as courier services operate on limited schedules.
            </div>
          </div>
        </div>
      </section>

      {/* INTERNATIONAL */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3">
            <Globe2 className="w-3.5 h-3.5" /> International
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#09090b] max-w-3xl">
            Worldwide shipping <span className="underline decoration-[#ea580c] decoration-4 underline-offset-4">available</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="international-tiers">
          {INTL_TIERS.map((t, idx) => {
            const Icon = t.icon;
            const isFree = t.price === "FREE";
            return (
              <div key={t.title} className={`p-8 border-2 ${isFree ? "border-[#09090b] bg-[#09090b] text-white" : "border-[#e7e5e4] bg-white"}`} data-testid={`intl-tier-${idx}`}>
                <div className="flex items-start justify-between gap-4">
                  <Icon className={`w-9 h-9 ${isFree ? "text-[#fb923c]" : "text-[#ea580c]"}`} strokeWidth={2.2} />
                  {t.price ? (
                    <span className={`text-xs font-black tracking-[0.15em] uppercase px-2.5 py-1 ${isFree ? "bg-[#fb923c] text-[#09090b]" : "bg-[#fef3c7] text-[#92400e] border border-[#fde68a]"}`}>
                      {t.price}
                    </span>
                  ) : null}
                </div>
                <div className={`mt-5 text-xs font-bold tracking-[0.2em] uppercase ${isFree ? "text-[#fb923c]" : "text-[#71717a]"}`}>{t.title}</div>
                <div className={`mt-1 font-display text-2xl font-black ${isFree ? "text-white" : "text-[#09090b]"}`}>{t.time}</div>
                <p className={`mt-3 text-sm leading-relaxed ${isFree ? "text-[#d4d4d8]" : "text-[#57534e]"}`}>{t.note}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#b91c1c] mb-3">Please Note</div>
            <h3 className="font-display text-2xl md:text-3xl font-black text-[#09090b] mb-5">
              Circumstances beyond our control
            </h3>
            <ul className="space-y-3 text-[#3f3f46]" data-testid="shipping-disclaimers">
              {[
                "We are not responsible for delays caused by customs clearance in the destination country",
                "Weather conditions, natural disasters, or transport strikes may impact schedules",
                "Carrier-side issues at the destination country are outside our control",
                "Estimated delivery times are not guarantees — actual times may vary",
                "During official holidays courier services operate on limited schedules",
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm">
                  <span className="mt-1 w-4 h-4 shrink-0 border-2 border-[#b91c1c] flex items-center justify-center">
                    <span className="w-2 h-0.5 bg-[#b91c1c]" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#09090b] text-white p-8 md:p-10">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#fb923c] mb-3">Need Order Status?</div>
            <h3 className="font-display text-2xl md:text-3xl font-black leading-tight">
              Track your parcel or ask about shipping charges — we reply fast.
            </h3>
            <p className="mt-4 text-sm text-[#a1a1aa] leading-relaxed">
              Keep your order number ready. Our team responds on WhatsApp within business hours and shares real-time updates from the courier.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href="https://wa.me/919953272630?text=Hi%20GAINA%20THE%20PRELUDE%2C%20I%20want%20to%20track%20my%20order."
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] text-white px-5 py-3 text-sm font-bold tracking-wide uppercase transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
              <a
                href="tel:9953272630"
                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-5 py-3 text-sm font-bold tracking-wide uppercase transition-colors"
              >
                Call Now
              </a>
            </div>
            <div className="mt-8 pt-6 border-t border-white/10 text-xs text-[#a1a1aa] leading-relaxed">
              GAINA THE PRELUDE • New Delhi, India<br />
              Email: gainatheprelude@gmail.com
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap gap-6 justify-center items-center text-sm">
          <Link to="/return-policy" className="inline-flex items-center gap-2 border-b-2 border-[#09090b] text-[#09090b] font-bold tracking-wide uppercase pb-1 hover:text-[#ea580c] hover:border-[#ea580c] transition-colors" data-testid="link-return-policy">
            Read Return Policy →
          </Link>
          <Link to="/books" className="inline-flex items-center gap-2 border-b-2 border-[#09090b] text-[#09090b] font-bold tracking-wide uppercase pb-1 hover:text-[#ea580c] hover:border-[#ea580c] transition-colors" data-testid="link-continue-shopping">
            Continue Shopping →
          </Link>
        </div>
      </section>
    </div>
  );
}
